import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useSendFeedbackMutation } from "@/redux/api/slack.rtkq";
import { useAuth } from "@/hooks/useAuth";
import { useParams } from "react-router";
import { useState, useEffect } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { createSlackBlocks } from "../utils/createSlackBlocks";
import type { Answer, FeedbackObject, OverviewSection } from "@/components/types";
import { toast } from "sonner";

const FeedbackButtons = ({ answer, userQuestion }: { answer: Answer; userQuestion: string }) => {
  const [sendFeedback, { isError, error, isSuccess, isLoading, reset }] = useSendFeedbackMutation();
  const { user } = useAuth();
  const { conversationId } = useParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");

  useEffect(() => {
    if (isSuccess) {
      toast.success("המשוב נשלח בהצלחה");
      reset();
    }
  }, [isSuccess, reset]);

  useEffect(() => {
    if (isError) {
      console.error(error);
      toast.error("שגיאה בשליחת המשוב", {
        description: "נסה שוב מאוחר יותר",
        duration: 5000,
      });
      reset();
    }
  }, [isError, error, reset]);

  // Get the overview section from the answer
  const overviewSection = answer.sections.find((section) => section.type === "overview") as OverviewSection | undefined;
  if (!overviewSection) return null;

  const handleLike = () => {
    if (!user || !conversationId) return;

    const feedbackObject: FeedbackObject = createSlackBlocks({
      feedbackType: "like",
      feedbackText,
      user,
      userQuestion,
      overviewSection,
      conversationId,
    });
    sendFeedback(feedbackObject);
  };

  const handleDislike = () => {
    setIsDialogOpen(true);
  };

  const handleSubmitFeedback = () => {
    if (!user || !conversationId) return;

    const feedbackObject: FeedbackObject = createSlackBlocks({
      feedbackType: "dislike",
      feedbackText,
      user,
      userQuestion,
      overviewSection,
      conversationId,
    });
    sendFeedback(feedbackObject);
    setIsDialogOpen(false);
    setFeedbackText("");
  };
  return (
    <div className="flex gap-3 max-md:gap-2 justify-end mb-4" dir="rtl">
      <Button
        disabled={isLoading}
        onClick={handleLike}
        variant="outline"
        className="flex items-center gap-2 px-4 py-2 max-md:px-3 max-md:py-2 rounded-lg border-slate-200 text-slate-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-all duration-200 shadow-sm group"
      >
        <ThumbsUp className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
        <span className="text-sm font-medium">שימושי</span>
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button
            disabled={isLoading}
            onClick={handleDislike}
            variant="outline"
            className="flex items-center gap-2 px-4 py-2 max-md:px-3 max-md:py-2 rounded-lg border-slate-200 text-slate-700 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-all duration-200 shadow-sm group"
          >
            <ThumbsDown className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
            <span className="text-sm font-medium">דורש שיפור</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right text-indigo-900 font-semibold">שתף את המשוב שלך</DialogTitle>
            <DialogDescription className="text-right text-slate-600">
              אנא ספר לנו מה לא עבד טוב כדי שנוכל לשפר את השירות
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="הקלד את המשוב שלך כאן..."
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="min-h-24 text-right"
              dir="rtl"
            />
          </div>
          <DialogFooter className="flex-row-reverse gap-3 max-md:gap-2">
            <Button
              onClick={handleSubmitFeedback}
              disabled={!feedbackText.trim()}
              className="bg-gradient-to-r from-sky-400 to-indigo-500 text-white rounded-xl px-6 py-3 max-md:px-4 max-md:py-2 shadow-[0_4.273px_12.819px_-4.273px_#789DF6] hover:shadow-[0_4.273px_12.819px_-4.273px_rgba(99,140,243,0.2)] transition-all duration-200"
            >
              שלח משוב
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setFeedbackText("");
              }}
              className="border-slate-200 text-slate-800 hover:bg-slate-100 rounded-xl px-6 py-3 max-md:px-4 max-md:py-2 transition-all duration-200"
            >
              ביטול
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackButtons;
